import React, { useContext, useMemo, useRef, useEffect } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import $ from 'jquery';
import { withServerContext, ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getTabbableNodes } from '@bingads-webui-react/focus-lock';
import { useP$DaterangeDefault } from './use-date-default';
import { inlineDownloadStyle } from './styles';

// TODO: support segments for inline download
export function useInlineDownload({
  processReportRule = _.identity,
  customDownloadService = undefined,
  downloadFormats = undefined,
  entity,
  totalRecordCount,
  validateSegmentType = () => true,
  isFeedbackEnabled = undefined,
  data = { SegmentationTypes: [] },
  InlineDownloadCampaignODataRepository,
  isForVNext = true,
  p$daterange = undefined,
  localizedHeaders,
  odataColumnNames,
  serverContext,
  filterUrlParam,
  serializedFilter,
}) {
  const p$daterangeDefaultValue = useP$DaterangeDefault();

  const p$daterangeDefault = () => p$daterangeDefaultValue;

  const segmentationTypes = [];

  if (serverContext && serverContext.Permissions && serverContext.Permissions.IsCustomerHierarchyEnabled) {
    _.extend(data, { IsCustomerHierachyRequest: true });
  }

  const repository = _.isFunction(customDownloadService) ? {
    download({
      Format,
      SegmentationTypes,
    }) {
      customDownloadService(Format, SegmentationTypes[0]);
      return Promise.resolve();
    },
  } : new InlineDownloadCampaignODataRepository({
    odataColumnNames,
    localizedHeaders,
    entity,
    filterUrlParam,
    serializedFilter,
    processReportRule,
    isForVNext,
    p$daterange: p$daterange || p$daterangeDefault,
  });

  return {
    isForVNext,
    totalRecordCount,
    repository,
    downloadFormats,
    segmentationTypes,
    validateSegmentType,
    isFeedbackEnabled,
    data,
    allUsersType: entity === 'Account' ? 'Customer' : 'Account',
  };
}

// Generate a inline download react from with default props and propTypes
const InlineDownloadWrapperInternal = React.memo((props) => {
  const serverContext = useContext(ServerContext);
  const classes = useDefaultStyles(inlineDownloadStyle);
  const inlineDownloadRef = useRef();
  const {
    setDownloadShown,
    getInlineDownloadColumns,
    getInlineDownloadFilter,
    ReportEmailRule,
    InlineDownloadReactForm,
    InlineDownloadCampaignODataRepository,
    totalAssetCount,
  } = props;

  const {
    localizedHeaders,
    odataColumnNames,
  } = useMemo(getInlineDownloadColumns, []);

  const {
    filterUrlParam,
    serializedFilter,
  } = useMemo(getInlineDownloadFilter, []);

  const inlineDownloadConfigs = useInlineDownload({
    serverContext,
    entity: 'Folder',
    totalRecordCount: totalAssetCount,
    downloadFormats: [
      'Csv',
      'Tsv',
      'Xlsx',
      'Csvzip',
    ],
    InlineDownloadCampaignODataRepository,
    validateSegmentType: () => true,
    isFeedbackEnabled: false,
    data: ReportEmailRule || ({ SegmentationTypes: [] }),
    isForVNext: true,
    p$daterange: undefined,
    localizedHeaders,
    odataColumnNames,
    filterUrlParam,
    serializedFilter,
  });

  useEffect(() => {
    if (inlineDownloadRef && inlineDownloadRef.current) {
      // filter the not visible element out which may due to parent display property
      const tabbableNode = _.filter(getTabbableNodes(inlineDownloadRef.current), item => $(item).is(':visible'));
      if (tabbableNode && tabbableNode.length > 0) {
        tabbableNode[0].focus({
          preventScroll: true,
        });
      }
    }
  });

  useEffect(() => () => {
    // focus on the download button after the inline download form closed
    const downloadButton = document.querySelector('#button-download');
    // wait for the download button rendered
    if (downloadButton && downloadButton.focus) {
      setTimeout(() => {
        downloadButton.focus();
      });
    }
  }, []);

  return (
    <div className={classNames(classes.InlineDownloadWrapper)} ref={inlineDownloadRef}>
      <InlineDownloadReactForm
        gridId={undefined} // this is related to cache mechanism
        toggleToolbarVisibility={() => setDownloadShown(false)}
        totalRecordCount={totalAssetCount}
        downloadFormats={inlineDownloadConfigs.downloadFormats}
        segmentationTypes={[]}
        validateSegmentType={() => true} // if we don't have segment type, we don't need to validate it
        isFeedbackEnabled={false}
        data={inlineDownloadConfigs.data}
        allUsersType={inlineDownloadConfigs.allUsersType}
        repository={inlineDownloadConfigs.repository}
        clearAfterDownloadCall={false}
        isForVNext
        downloadBoxRole="listbox"
        downloadFormatsRole="option"
      />
    </div>
  );
});

InlineDownloadWrapperInternal.propTypes = {
  setDownloadShown: PropTypes.func,
  totalAssetCount: PropTypes.number.isRequired,
  ReportEmailRule: PropTypes.shape({}),
  getInlineDownloadColumns: PropTypes.func.isRequired,
  getInlineDownloadFilter: PropTypes.func.isRequired,
  InlineDownloadReactForm: PropTypes.element.isRequired,
  InlineDownloadCampaignODataRepository: PropTypes.func.isRequired,
};

InlineDownloadWrapperInternal.defaultProps = {
  setDownloadShown: () => {},
  ReportEmailRule: undefined,
};

const DelayedInlineDownloadWrapperInternal = React.lazy(() =>
  import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-legacy/griddownload/view/loader')
    .then(({ InlineDownloadReactForm, InlineDownloadCampaignODataRepository }) => ({
      default: React.memo(props => (<InlineDownloadWrapperInternal
        {...props}
        InlineDownloadReactForm={InlineDownloadReactForm}
        InlineDownloadCampaignODataRepository={InlineDownloadCampaignODataRepository}
      />)),
    })));

export const InlineDownloadWrapper = withServerContext(DelayedInlineDownloadWrapperInternal);
